import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { text_commonCellProps, select_commonCellProps } from '../../Common/DataGrid/utils';
import { StyledCellLink } from '../../Common/DataGrid/CellComponents';
import StatusChip from '../StatusChip';
import { formatAbsRelDate, formatAbsoluteDate, formatLbs, formatPercent } from '../formatters';

// AB#17128: client half of each surface's columns (AC5-AC10 default order, then custom); fields match ComplianceColumns.cs

const from = () => window.location.pathname;

const link = (path, text, primaryLink = false) => (
    <StyledCellLink path={path} text={text} from={from()} primaryLink={primaryLink} />
);

const text = (field, headerName, extra = {}) => ({ field, headerName, ...text_commonCellProps, ...extra });

const select = (field, headerName, valueOptions, extra = {}) => ({ field, headerName, ...select_commonCellProps, valueOptions, ...extra });

const display = (field, headerName, render, extra = {}) => ({ field, headerName, flex: 1, filterable: false, renderCell: ({ row }) => render(row), ...extra });

const custom = (column) => ({ ...column, initiallyVisible: false });

// Obligation-anchoring dates link to their source service event (AC12); fixed width so "date · N days ago" isn't truncated
const eventDate = (field, headerName, eventField, blank = '', extra = {}) => display(field, headerName, (row) => {
    if (!row[field]) return typeof blank === 'function' ? blank(row) : blank;
    const label = formatAbsRelDate(row[field]);
    return row[eventField] ? link(`/ServiceEvent/${row[eventField]}`, label) : label;
}, { flex: 0, width: 250, ...extra });

// Computed due dates also render absolute plus relative (AC12)
const dueDate = (field, headerName, blank = '', extra = {}) => display(field, headerName, (row) => (row[field] ? formatAbsRelDate(row[field]) : blank), { flex: 0, width: 250, ...extra });

const eventLink = (field, headerName) => display(field, headerName, (row) => (row[field] ? link(`/ServiceEvent/${row[field]}`, `#${row[field]}`) : ''));

const assetName = (headerName = 'Asset Name') => text('assetName', headerName, {
    renderCell: ({ row }) => link(`/Inventory/${row.assetId}`, row.assetName, true),
});

const location = () => text('locationName', 'Location', {
    renderCell: ({ row }) => (row.locationName ? link(`/Location/${row.locationId}`, row.locationName) : ''),
});

const status = (kind, headerName, valueOptions) => select('status', headerName, valueOptions, {
    renderCell: ({ row }) => <StatusChip label={row.status} tone={row.statusTone} kind={kind} />,
});

// Spec format column: thresholds and calendar-year percentages are whole numbers ("20 %", "125 %")
const wholePercent = (value) => formatPercent(value, 0);

// Equipment Type tooltip carries the regulation-specific class (Spec §2)
const equipmentType = (headerName = 'Equipment Type') => text('equipmentType', headerName, {
    renderCell: ({ row }) => (row.equipmentClass
        ? <Tooltip title={row.equipmentClass}><span>{row.equipmentType}</span></Tooltip>
        : row.equipmentType),
});

const state = () => text('state', 'State');
const tagNumber = () => custom(text('tagNumber', 'Tag Number'));
const assetId = () => custom(display('assetId', 'Asset ID', (row) => row.assetId));
const refrigerantType = () => custom(text('refrigerantType', 'Refrigerant Type'));
const fullCharge = () => custom(display('fullCharge', 'Full Charge', (row) => formatLbs(row.fullCharge)));
const regionDivision = () => custom(text('regionDivision', 'Region / Division'));
const systemManager = () => custom(text('systemManager', 'System Manager'));

const inScopeColumns = ({ descriptions }) => [
    assetName(),
    text('applicableRegulation', 'Applicable Regulation'),
    text('operationalStatus', 'Operational Status'),
    text('equipmentType', 'Type'),
    text('refrigerantType', 'Material Type'),
    display('fullCharge', 'Gas Capacity Charge', (row) => formatLbs(row.fullCharge)),
    display('leakRatePercent', 'Leak Rate %', (row) => formatPercent(row.leakRatePercent), { description: descriptions.leakRatePercent }),
    text('materialClass', 'Material Class'),
    tagNumber(),
    custom(text('roomArea', 'Room / Area')),
    custom(text('inventoryStatus', 'Status')),
    custom(text('temperatureClass', 'Temperature Class')),
    custom(text('locationOnSite', 'Location on Site', {
        renderCell: ({ row }) => (row.locationOnSite ? link(`/Location/${row.locationId}`, row.locationOnSite) : ''),
    })),
    custom(display('currentGasWeight', 'Current Gas Weight', (row) => formatLbs(row.currentGasWeight))),
    custom(text('manufacturer', 'Manufacturer')),
    custom(text('modelNumber', 'Model Number')),
    custom(text('serialNumber', 'Serial Number')),
];

const leakRateColumns = ({ descriptions, valueOptions }) => [
    status('leakRate', 'Band', ['Hit', 'Near', 'Low']),
    display('highestLeakRate', 'Highest Leak Rate', (row) => formatPercent(row.highestLeakRate), { description: descriptions.highestLeakRate }),
    assetName(),
    eventDate('exceedanceDate', 'Exceedance Date', 'exceedanceEventId'),
    eventLink('exceedanceEventId', 'Exceedance Event'),
    display('thresholdPercent', 'Threshold', (row) => wholePercent(row.thresholdPercent)),
    select('calcMethod', 'Calc Method', valueOptions.calcMethod || []),
    location(),
    state(),
    custom(equipmentType()),
    custom(display('refrigerantAdded', 'Refrigerant Added', (row) => formatLbs(row.refrigerantAdded))),
    tagNumber(),
    assetId(),
    refrigerantType(),
    fullCharge(),
    regionDivision(),
    systemManager(),
];

const inspectionColumns = ({ descriptions, valueOptions }) => [
    status('inspection', 'Status', ['Hit', 'Near']),
    assetName(),
    eventDate('lastInspectionDate', 'Last Inspection Date', 'lastInspectionEventId', 'No inspection on record'),
    eventLink('lastInspectionEventId', 'Leak Inspection Service Event'),
    dueDate('nextDue', 'Next Due', '—', { description: descriptions.nextDue }),
    // AC7: cadence is a chip read from the tag, still filterable
    select('requiredCadence', 'Required Cadence', valueOptions.requiredCadence || [], {
        renderCell: ({ row }) => (row.requiredCadence ? <Chip label={row.requiredCadence} size="small" variant="outlined" sx={{ fontWeight: 600 }} /> : ''),
    }),
    // Spec §4 #7: Trigger Basis is a chip + date
    text('triggerBasis', 'Trigger Basis', {
        renderCell: ({ row }) => (
            <Box component="span">
                {row.triggerBasis && <Chip label={row.triggerBasis} size="small" variant="outlined" sx={{ fontWeight: 600 }} />}
                {row.exceedanceDate && ' '}
                {row.exceedanceDate && (row.exceedanceEventId
                    ? link(`/ServiceEvent/${row.exceedanceEventId}`, formatAbsoluteDate(row.exceedanceDate))
                    : formatAbsoluteDate(row.exceedanceDate))}
            </Box>
        ),
    }),
    state(),
    location(),
    equipmentType(),
    tagNumber(),
    assetId(),
    refrigerantType(),
    fullCharge(),
    custom(text('monitoring', 'Monitoring')),
    regionDivision(),
    systemManager(),
];

const repairColumns = ({ descriptions }) => [
    status('repair', 'Status', ['Hit', 'Near']),
    display('daysSinceExceedance', 'Days Since Exceedance', (row) => row.daysSinceExceedance ?? ''),
    assetName(),
    eventDate('exceedanceDate', 'Exceedance Date', 'exceedanceEventId'),
    eventLink('exceedanceEventId', 'Exceedance Service Event'),
    dueDate('repairDue', 'Repair Due', '', { description: descriptions.repairDue }),
    // Spec §5 #7: blank = still open
    eventDate('repairDate', 'Repair Date / Completed', 'repairEventId', '— Open'),
    location(),
    state(),
    custom(equipmentType()),
    tagNumber(),
    assetId(),
    refrigerantType(),
    fullCharge(),
    regionDivision(),
    systemManager(),
];

const aldCalibrationColumns = () => [
    status('aldCalibration', 'Status', ['Hit', 'Near']),
    assetName('Device'),
    eventDate('lastCalibrationDate', 'Last Calibration', 'lastCalibrationEventId', 'Never calibrated'),
    dueDate('calibrationDue', 'Calibration Due', 'Overdue since install'),
    // H1: the ALD-lapse reversion (ADO 16783) isn't built, so the cell counts monitored in-scope assets without promising it
    display('complianceImpact', 'Compliance Impact', (row) => (row.complianceImpact
        ? (
            <Tooltip title="In-scope assets that rely on this device for leak monitoring">
                <span>{`${row.complianceImpact} monitored in-scope ${row.complianceImpact === 1 ? 'asset' : 'assets'}`}</span>
            </Tooltip>
        )
        : '—'), { flex: 0, width: 260 }),
    text('linkedAssetsText', 'Linked Assets', {
        renderCell: ({ row }) => (row.linkedAssets || []).map((asset, i) => (
            <React.Fragment key={asset.id}>
                {i > 0 && ', '}
                {link(`/Inventory/${asset.id}`, asset.name)}
            </React.Fragment>
        )),
    }),
    location(),
    tagNumber(),
    assetId(),
    regionDivision(),
    systemManager(),
];

const chronicLeakColumns = () => {
    const year = new Date().getFullYear();
    return [
        status('chronicLeak', 'Status', ['Hit', 'Near', 'Low']),
        select('calendarYear', 'Calendar Year', [String(year), String(year - 1)]),
        display('currentCyPercent', 'Current CY %', (row) => wholePercent(row.currentCyPercent)),
        assetName(),
        display('refrigerantAddedYtd', 'Refrigerant Added YTD', (row) => formatLbs(row.refrigerantAddedYtd)),
        display('projectedYearEndPercent', 'Projected Year-End %', (row) => {
            if (row.projectedYearEndPercent === null || row.projectedYearEndPercent === undefined) return '—';
            return row.projectedYearEndPercent > 300 ? '>300%' : wholePercent(row.projectedYearEndPercent);
        }),
        display('thresholdPercent', 'Threshold (125%)', (row) => wholePercent(row.thresholdPercent)),
        location(),
        equipmentType(),
        custom(dueDate('reportDeadline', 'Report Deadline')),
        custom(text('reportStatus', 'Report Status', { renderCell: ({ row }) => row.reportStatus || '—' })),
        tagNumber(),
        assetId(),
        refrigerantType(),
        fullCharge(),
        regionDivision(),
        systemManager(),
        custom(state()),
    ];
};

// Regulation-specific header descriptions and select options come from config, so these columns stay regulation-neutral
export const columnsForKind = (kind, { descriptions = {}, valueOptions = {} } = {}) => {
    const options = { descriptions, valueOptions };
    switch (kind) {
        case 'inScope': return inScopeColumns(options);
        case 'leakRate': return leakRateColumns(options);
        case 'inspection': return inspectionColumns(options);
        case 'repair': return repairColumns(options);
        case 'aldCalibration': return aldCalibrationColumns();
        case 'chronicLeak': return chronicLeakColumns();
        default: return [];
    }
};
